import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { SafeDialog } from "components/Dialog";
import { ImportChangeSummary } from "generated-graphql/graphql";
import pluralize from "pluralize";

// pluralize() restores the input word's case onto its suffix, so an acronym
// comes back wrong ("LEPC" → "LEPCS"). Acronyms pluralize with a lowercase
// "s"; list them explicitly rather than inferring from casing.
const ACRONYM_LABELS = new Set(["LEPC"]);

function pluralizeLabel(label: string, count: number): string {
  if (count === 1) return label;
  return ACRONYM_LABELS.has(label) ? `${label}s` : pluralize(label);
}

export interface ConfirmImportDialogProps {
  open: boolean;
  /** `entityLabel` is the singular noun ("Facility"); the dialog pluralizes it per count. */
  preview: Pick<
    ImportChangeSummary,
    "entityLabel" | "recordsAffected" | "added" | "edited" | "deleted"
  > | null;
  /** True while the preview counts are still being computed (dialog opens immediately). */
  previewLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

export function ConfirmImportDialog({
  open,
  preview,
  previewLoading,
  onConfirm,
  onCancel,
  loading,
}: ConfirmImportDialogProps) {
  const showCounts = !previewLoading && preview !== null;

  return (
    <SafeDialog open={open}>
      <DialogTitle>Confirm import</DialogTitle>
      <DialogContent>
        {showCounts ? (
          <Stack spacing={2}>
            <DialogContentText>Review the changes below and confirm to proceed.</DialogContentText>
            <Stack spacing={1}>
              <Typography variant="body1">
                {(preview.added + preview.edited).toLocaleString()}{" "}
                {pluralize("field", preview.added + preview.edited)} added or edited
              </Typography>
              <Typography variant="body1">
                {preview.deleted.toLocaleString()} {pluralize("field", preview.deleted)} deleted
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {preview.recordsAffected.toLocaleString()}{" "}
                {pluralizeLabel(preview.entityLabel, preview.recordsAffected)}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress />
            <DialogContentText>Analyzing changes…</DialogContentText>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading || previewLoading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" loading={loading} disabled={!showCounts}>
          Confirm
        </Button>
      </DialogActions>
    </SafeDialog>
  );
}
